import { buildPieItems } from './pie-chart-utils';

type TickLabelStyle = {
  angle?: number;
  textAnchor?: 'start' | 'middle' | 'end';
};

export type AxisProps = Record<string, unknown> & {
  tick?: Record<string, unknown>;
  tickLabelStyle?: TickLabelStyle;
};

export type ChartSpec = {
  kind: 'viz';
  spec: {
    component: string;
    title?: string;
    data: Array<Record<string, unknown>>;
    xKey: string;
    yKeys: string[];
    colors?: string[];
    xAxisProps?: AxisProps;
    yAxisProps?: AxisProps;
    seriesTypes?: string[];
  };
};

export type ParsedContent =
  | { type: 'thinking'; content: string }
  | { type: 'text'; content: string }
  | { type: 'chart'; content: ChartSpec }
  | { type: 'warning'; content: string };

const SYSTEM_WARNING_PATTERNS = [/^Agent stopped due to max iterations\.?$/i];

/**
 * Regex to match thinking blocks with XML-style tags.
 */
const THINKING_BLOCK_REGEX = /<thinking>([\s\S]*?)<\/thinking>/g;

/**
 * Regex to match markdown JSON code blocks.
 * Captures the JSON content inside ```json ... ```
 */
const JSON_CODE_BLOCK_REGEX = /```json\s*\n([\s\S]*?)\s*```/g;

/**
 * Patterns for detecting chart JSON start positions.
 * Exported for use by incomplete block detector to stay in sync.
 */
export const CHART_START_PATTERNS = {
  legacy: /\{"kind":"viz"/,
  bar: /\{"type":"bar"/,
  line: /\{"type":"line"/,
  pie: /\{"type":"pie"/,
};

/**
 * LLM chart format as returned by Databricks.
 * This is the raw format that needs to be transformed to ChartSpec.
 * Pie charts are supported only for a narrow shape: one x field, one y field,
 * and flat rows with finite numeric values.
 */
type CartesianLLMChartFormat = {
  type: 'bar' | 'line';
  x: string;
  y: string | string[];
  title?: string;
  data: Array<Record<string, unknown>>;
};

type PieLLMChartFormat = {
  type: 'pie';
  x: string;
  y: string;
  title?: string;
  data: Array<Record<string, unknown>>;
};

type LLMChartFormat = CartesianLLMChartFormat | PieLLMChartFormat;

/**
 * Map LLM chart type to Recharts component name.
 */
const CHART_TYPE_MAP: Record<LLMChartFormat['type'], string> = {
  bar: 'BarChart',
  line: 'LineChart',
  pie: 'PieChart',
};

function isCartesianLLMChartFormat(
  chart: Record<string, unknown>,
): chart is CartesianLLMChartFormat {
  const isValidY =
    typeof chart.y === 'string' ||
    (Array.isArray(chart.y) &&
      chart.y.length > 0 &&
      chart.y.every((item) => typeof item === 'string'));

  return (
    (chart.type === 'bar' || chart.type === 'line') &&
    typeof chart.x === 'string' &&
    isValidY &&
    Array.isArray(chart.data)
  );
}

function isPieLLMChartFormat(chart: Record<string, unknown>): chart is PieLLMChartFormat {
  if (
    chart.type !== 'pie' ||
    typeof chart.x !== 'string' ||
    typeof chart.y !== 'string' ||
    !Array.isArray(chart.data)
  ) {
    return false;
  }

  return buildPieItems(chart.data, chart.x, chart.y) !== null;
}

/**
 * Check if parsed JSON matches the LLM chart format.
 * Accepts bar/line and a narrowly validated pie shape.
 */
function isLLMChartFormat(obj: unknown): obj is LLMChartFormat {
  if (!obj || typeof obj !== 'object') return false;
  const chart = obj as Record<string, unknown>;

  if (chart.type === 'pie') {
    return isPieLLMChartFormat(chart);
  }

  return isCartesianLLMChartFormat(chart);
}

/**
 * Transform LLM chart format to ChartSpec format expected by ResponseChart.
 */
function transformLLMChartToChartSpec(llmChart: LLMChartFormat): ChartSpec {
  return {
    kind: 'viz',
    spec: {
      component: CHART_TYPE_MAP[llmChart.type] || 'BarChart',
      title: llmChart.title,
      data: llmChart.data,
      xKey: llmChart.x,
      yKeys: Array.isArray(llmChart.y) ? llmChart.y : [llmChart.y],
    },
  };
}

/**
 * Try to parse and validate chart JSON, returning ChartSpec if valid.
 * Handles both the legacy {"kind":"viz"} format and LLM {"type":"bar"/"line"/"pie"} format.
 */
function tryParseChartJson(jsonString: string): ChartSpec | null {
  try {
    const parsed = JSON.parse(jsonString) as unknown;

    // Check for legacy format: {"kind":"viz","spec":{...}}
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed as Record<string, unknown>).kind === 'viz' &&
      (parsed as Record<string, unknown>).spec
    ) {
      return parsed as ChartSpec;
    }

    // Check for LLM format: {"type":"bar"|"line"|"pie","x":"...","y":"...","data":[...]}
    if (isLLMChartFormat(parsed)) {
      return transformLLMChartToChartSpec(parsed);
    }

    return null;
  } catch {
    return null;
  }
}

export function parseResponseContent(content: string): ParsedContent[] {
  const result: ParsedContent[] = [];
  let remainingContent = content;

  const thinkingMatches = Array.from(content.matchAll(THINKING_BLOCK_REGEX));

  thinkingMatches.forEach((match) => {
    const matchIndex = remainingContent.indexOf(match[0]);
    const beforeThinking = remainingContent.substring(0, matchIndex);
    if (beforeThinking.trim()) {
      const textParts = extractTextAndCharts(beforeThinking);
      result.push(...textParts);
    }
    // Thinking blocks are stripped entirely -- the ThinkingIndicator at the
    // top of the message already shows the streaming status / "Thought for Xs"
    // based on streaming state, so we don't need the content.
    remainingContent = remainingContent.substring(matchIndex + match[0].length);
  });

  if (remainingContent.trim()) {
    const textParts = extractTextAndCharts(remainingContent);
    result.push(...textParts);
  }

  return result;
}

function extractBalancedJson(text: string, startIndex: number): string | null {
  let braceCount = 0;
  let inString = false;
  let escape = false;

  for (let i = startIndex; i < text.length; i++) {
    const char = text[i];

    if (escape) {
      escape = false;
      continue;
    }

    if (char === '\\' && inString) {
      escape = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === '{') braceCount++;
      if (char === '}') braceCount--;

      if (braceCount === 0) {
        return text.substring(startIndex, i + 1);
      }
    }
  }

  return null;
}

const isSystemWarning = (text: string): boolean =>
  SYSTEM_WARNING_PATTERNS.some((pattern) => pattern.test(text.trim()));

const createTextOrWarning = (content: string): ParsedContent =>
  isSystemWarning(content) ? { type: 'warning', content } : { type: 'text', content };

/**
 * Extract markdown JSON code blocks, preserving their positions regardless of
 * whether they contain a supported chart payload.
 */
function extractMarkdownJsonBlocks(
  text: string,
): Array<{ start: number; end: number; content: string; chartSpec: ChartSpec | null }> {
  const blocks: Array<{
    start: number;
    end: number;
    content: string;
    chartSpec: ChartSpec | null;
  }> = [];
  const regex = new RegExp(JSON_CODE_BLOCK_REGEX.source, 'g');
  let match;

  while ((match = regex.exec(text)) !== null) {
    const jsonContent = match[1].trim();
    blocks.push({
      start: match.index,
      end: match.index + match[0].length,
      content: match[0],
      chartSpec: tryParseChartJson(jsonContent),
    });
  }

  return blocks;
}

/**
 * Minimum length for a text fragment to be considered a potential duplicate
 * of a preceding code block. Short strings could match by coincidence.
 */
const DUPLICATE_FRAGMENT_MIN_LENGTH = 10;

/**
 * Check whether the remaining text after the last markdown code block is a
 * duplicate fragment of that code block's content. The backend occasionally
 * appends a trailing copy of the code block's tail (including stray closing
 * backtick fences). We detect this by stripping backtick fences from the
 * remaining text and checking if the result appears verbatim inside the
 * preceding code block.
 */
function isDuplicateCodeBlockFragment(
  remainingText: string,
  fullText: string,
  charts: Array<{ start: number; end: number }>,
): boolean {
  const lastChart = charts[charts.length - 1];
  const codeBlockText = fullText.substring(lastChart.start, lastChart.end);
  const strippedRemaining = remainingText.replace(/`{3,}/g, '').trim();

  return (
    strippedRemaining.length >= DUPLICATE_FRAGMENT_MIN_LENGTH &&
    codeBlockText.includes(strippedRemaining)
  );
}

/**
 * Extract text and chart blocks from content.
 * Charts are identified by:
 * 1. Markdown JSON code blocks (```json ... ```) - primary format from backend
 * 2. Raw JSON with {"kind":"viz" or {"type":"bar"/"line"/"pie" patterns - legacy/fallback
 */
function extractTextAndCharts(text: string): ParsedContent[] {
  const result: ParsedContent[] = [];

  // First, process markdown JSON code blocks. Unsupported blocks stay as text.
  const markdownBlocks = extractMarkdownJsonBlocks(text);

  if (markdownBlocks.length > 0) {
    let lastEnd = 0;
    const chartBlocks = markdownBlocks
      .filter(
        (block): block is { start: number; end: number; content: string; chartSpec: ChartSpec } =>
          block.chartSpec !== null,
      )
      .map(({ start, end }) => ({ start, end }));

    for (const block of markdownBlocks) {
      const beforeBlock = text.substring(lastEnd, block.start);
      if (beforeBlock.trim()) {
        // Process the text before for any raw JSON charts (backwards compatibility)
        const textParts = extractRawJsonCharts(beforeBlock);
        result.push(...textParts);
      }

      if (block.chartSpec) {
        result.push({ type: 'chart', content: block.chartSpec });
      } else {
        result.push(createTextOrWarning(block.content.trim()));
      }

      lastEnd = block.end;
    }

    // Add remaining text after the last chart, unless it is a duplicate
    // fragment of the preceding code block (backend occasionally appends a
    // trailing copy of the code block's tail).
    const remainingText = text.substring(lastEnd);
    if (
      remainingText.trim() &&
      !(chartBlocks.length > 0 && isDuplicateCodeBlockFragment(remainingText, text, chartBlocks))
    ) {
      const textParts = extractRawJsonCharts(remainingText);
      result.push(...textParts);
    }

    return result;
  }

  // Fallback to raw JSON detection for backwards compatibility
  return extractRawJsonCharts(text);
}

/**
 * Extract charts from raw JSON patterns in text.
 * Legacy format: {"kind":"viz"...} or {"type":"bar"/"line"/"pie"...}
 */
function extractRawJsonCharts(text: string): ParsedContent[] {
  const result: ParsedContent[] = [];
  let remainingText = text;

  // Match both legacy format and LLM format
  const chartStartRegex = /\{"kind":"viz"|\{"type":"(?:bar|line|pie)"/g;
  let match;

  while ((match = chartStartRegex.exec(remainingText)) !== null) {
    const startIndex = match.index;
    const jsonString = extractBalancedJson(remainingText, startIndex);

    if (!jsonString) {
      // Unbalanced JSON - include everything including the broken JSON as text
      if (remainingText.trim()) {
        result.push(createTextOrWarning(remainingText.trim()));
      }
      return result;
    }

    const beforeChart = remainingText.substring(0, startIndex);
    if (beforeChart.trim()) {
      result.push(createTextOrWarning(beforeChart.trim()));
    }

    const chartSpec = tryParseChartJson(jsonString);
    if (chartSpec) {
      result.push({ type: 'chart', content: chartSpec });
    } else {
      // Valid JSON but not a chart - include it as text
      result.push(createTextOrWarning(jsonString));
    }

    remainingText = remainingText.substring(startIndex + jsonString.length);
    chartStartRegex.lastIndex = 0;
  }

  if (remainingText.trim()) {
    result.push(createTextOrWarning(remainingText.trim()));
  }

  return result;
}
